import {Component, effect, inject, signal} from '@angular/core';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {Location} from '@angular/common';
import {BadgeUserData, PersonPickerComponent} from '../common/person-picker/person-picker.component';
import {MatDialog} from '@angular/material/dialog';
import {ContactTable} from './contact-table/contact-table.component';
import {Contact, ContactCountsResponse} from '../../models/contact';
import {ContactService} from '../services/contact.service';
import {MatSelect, MatSelectChange} from '@angular/material/select';
import {ContactDialogData, CreateContactComponent} from './create-contact/create-contact.component';
import {User} from '../../models/user';
import {ItemCounterComponent} from '../common/item-counter/item-counter.component';
import {StatusGroup} from '../common/status-parser.pipe';
import {ActivatedRoute, Router} from '@angular/router';
import {SnackbarService} from '../services/snackbar.service';
import {ButtonWLoaderComponent} from '../common/button-w-loader/button-w-loader.component';
import {MatTooltip} from '@angular/material/tooltip';
import {PageEvent} from '@angular/material/paginator';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

@Component({
  selector: 'app-contacts',
  imports: [
    MatFormField,
    MatInput,
    MatLabel,
    PersonPickerComponent,
    ContactTable,
    MatSelect,
    MatOption,
    ItemCounterComponent,
    ButtonWLoaderComponent,
    MatTooltip
  ],
  templateUrl: './contacts.component.html',
  styleUrl: './contacts.component.scss'
})
export class ContactsComponent {
  statusMap = new Map<string, StatusGroup>([
    ['complete', StatusGroup.complete],
    ['incomplete', StatusGroup.incomplete],
  ]);

  snackBar = inject(SnackbarService);
  activatedRoute = inject(ActivatedRoute);
  contactService = inject(ContactService);
  router = inject(Router);

  contactLimit = 30000;
  pageSize = signal(10);

  cId: string | null = '';

  private searchSubject = new Subject<string>();

  constructor() {
    this.activatedRoute.paramMap.subscribe((param) => {
      this.cId = param.get('contactId');
      this.update();
    });
    effect(() => {
      // Read signals to track them
      const filters = this.contactService.contactFilters();
      const contacts = this.contactService.contactList();
      const counts = this.contactService.contactCounts();

      // Get users from filters or extract from contacts
      if (filters?.users?.length) {
        this.usersToShowBadges = filters.users.map(u => ({ id: u.id, name: u.name, badgeColor: u.badgeColor } as User));
      } else {
        this.usersToShowBadges = this.extractUsersFromContacts(contacts);
      }

      this.setStatusMap(counts);
      if (this.cId) {
        const cId = this.cId;
        this.cId = null;
        const contact = contacts.find((c) => c.id === cId);
        if (contact) {
          this.openDialog(cId);
        } else {
          this.contactService.getContactById(cId).then(contactData => {
            this.openDialogWithData(contactData);
          }).catch(() => {
            this.snackBar.openSnackBar('error', 'Contato não encontrado', 'OK');
            this.location.go('contatos');
          });
        }
      }
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.contactService.getContactsPaginated({
        search: searchValue || undefined,
        users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
        type: this.selectedType || undefined,
        offset: 0,
        limit: this.pageSize()
      });
    });
  }

  setStatusMap(counts: ContactCountsResponse | null) {
    this.statusMap.get('complete')?.setCount(counts?.complete ?? 0);
    this.statusMap.get('incomplete')?.setCount(counts?.incomplete ?? 0);
  }

  private extractUsersFromContacts(contacts: Contact[]): User[] {
    const userMap = new Map<string, User>();
    for (const contact of contacts) {
      if (contact.assigneeUser && !userMap.has(contact.assigneeUser.id)) {
        userMap.set(contact.assigneeUser.id, contact.assigneeUser);
      }
    }
    return Array.from(userMap.values());
  }

  opened = false;

  location = inject(Location);

  update() {
    // Called from route subscription - effect handles the rest
  }

  usersToShowBadges?: User[];

  readonly dialog = inject(MatDialog);

  openDialog(contactId?: string): void {
    const contactData = contactId ? this.contactService.contactList().find((contact) => contact.id === contactId) : undefined;
    this.openDialogWithData(contactData);
  }

  private openDialogWithData(contactData?: Contact): void {
    if (this.opened) {
      return;
    }
    this.opened = true;
    const dialogRef = this.dialog.open<CreateContactComponent, ContactDialogData, { id: string } | undefined>(CreateContactComponent, {
      maxWidth: '1100px',
      height: '680px',
      data: { contactData },
    });

    dialogRef.afterClosed().subscribe(async result => {
      this.cId = null;
      if (result?.id) {
        const snack = this.snackBar.openSnackBar('success', 'Contato criado com sucesso!', 'Clique para vê-lo',)
        snack.onAction().subscribe(() => {
          this.router.navigate(['contatos', result.id]);
        })
      } else {
        this.location.go('contatos');
      }
      this.opened = false;
    });
  }

  searchValue: string = '';
  selectedType: string = '';

  newContact() {
    this.openDialog();
  }

  openDetails(contactId: string) {
    this.openDialog(contactId);
  }

  search(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.searchSubject.next(filterValue);
  }

  selectedPeople: string[] = [];

  personSelected(event: BadgeUserData[]) {
    this.selectedPeople = event.filter((user) => user.selected).map((user) => user.id);
    this.contactService.getContactsPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      type: this.selectedType || undefined,
      offset: 0,
      limit: this.pageSize()
    });
  }

  typeSelected(event: MatSelectChange) {
    this.selectedType = event.value === '__all__' ? '' : event.value;
    this.contactService.getContactsPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      type: this.selectedType || undefined,
      offset: 0,
      limit: this.pageSize()
    });
  }

  onPageChange(event: PageEvent) {
    this.pageSize.set(event.pageSize);
    this.contactService.getContactsPaginated({
      search: this.searchValue || undefined,
      users: this.selectedPeople.length > 0 ? this.selectedPeople : undefined,
      type: this.selectedType || undefined,
      offset: event.pageIndex * event.pageSize,
      limit: event.pageSize
    });
  }
}
